import React, { FC, memo, useCallback, useState } from 'react'
import { HStack, Flex, Input } from '@chakra-ui/react'
import { PhoneSpecsCondensed } from '../../phones'
import { PhoneListCondensedProps } from './phone-list-condensed'
import { IconButton } from '../icon-button'
import { CustomSortIcon } from '../icon'
import { Dropdown } from '../dropdown'
import i18next from 'i18next'
import { SortOptionCondensed } from '../../state/interfaces'
import { useDispatch } from 'react-redux'
import { setSearchText } from '../../state/search-text-slice'

export interface SortedPhoneListCondensedProps extends PhoneListCondensedProps {
  PhoneListCondensed: React.ComponentType<PhoneListCondensedProps>
}

const getSortOptionValue = (value: string) => Object.entries(SortOptionCondensed).find(([key]) => key === value)?.[1]

const sortOptions = Object.entries(SortOptionCondensed).reduce((accumulator, [value, label]) => {
  const translatedLabel = i18next.t(label)
  return [...accumulator, { label: translatedLabel, value }]
}, [] as Array<{ label: string; value: string }>)

export const SortedPhoneListCondensed: FC<SortedPhoneListCondensedProps> = memo(
  ({ PhoneListCondensed, phones, onClick }) => {
    const [sortBy, setSortBy] = useState<SortOptionCondensed>(SortOptionCondensed.POPULARITY)
    const [sortInAscendingOrder, setSortInAscendingOrder] = useState(true)
    const [filterQuery, setFilterQuery] = useState('')
    const dispatch = useDispatch()

    const saveSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setSearchText(e.target.value))
      setFilterQuery(e.target.value)
    }

    const toggleSortOrder = useCallback(() => {
      setSortInAscendingOrder(!sortInAscendingOrder)
    }, [sortInAscendingOrder])

    const changeSortBy = (sortOption: SortOptionCondensed) => {
      setSortBy(sortOption)
    }

    const sortPhones = useCallback(
      (phones: PhoneSpecsCondensed[]) => {
        const normalizedQuery = filterQuery.trim().toLowerCase()
        const filteredPhones =
          normalizedQuery && normalizedQuery !== 'nc'
            ? phones.filter((p) => p.name.toLowerCase().includes(normalizedQuery))
            : phones

        const sortedPhones = [...filteredPhones].sort((a, b) => {
          switch (getSortOptionValue(sortBy)) {
            case SortOptionCondensed.PHONE_PRICE:
              return b.avgModelPriceTier - a.avgModelPriceTier
            case SortOptionCondensed.PHONE_NAME:
              return b.name.localeCompare(a.name)
            default:
            case SortOptionCondensed.POPULARITY:
              if (b.position === a.position) {
                return b.avgModelPriceTier - a.avgModelPriceTier
              }
              return b.position - a.position
          }
        })

        if (sortInAscendingOrder) {
          sortedPhones.reverse()
        }

        return sortedPhones
      },
      [sortBy, sortInAscendingOrder, filterQuery],
    )

    return (
      <>
        {phones.length > 0 && (
          <Flex
            width="100%"
            paddingTop={4}
            flexDirection={{ base: 'column', md: 'row' }}
            alignItems={{ base: 'center', md: '' }}
          >
            <HStack
              width={{ base: 'auto', md: 'full' }}
              justifyContent="flex-start"
              marginRight={2}
              marginBottom={{ base: 2, md: 0 }}
            >
              <Input
                placeholder={i18next.t('search')}
                value={filterQuery}
                onChange={(e) => saveSearchText(e)}
                borderRadius="medium"
                fontWeight="normal"
                fontSize={18}
                border={2}
                borderStyle="solid"
                borderColor="grey"
                width={240}
                height={50}
                // color="black"
                backgroundColor="white"
                _hover={{
                  borderColor: 'brand',
                  boxShadow: '0 0 0 3px #5bce4c',
                }}
                _focus={{
                  borderColor: 'brand',
                  boxShadow: '0 0 0 2px #5bce4c',
                }}
                _placeholder={{
                  color: 'black',
                }}
                aria-label="search-phones"
              />
            </HStack>
            <HStack
              width={{ base: 'auto', md: 'full' }}
              justifyContent="flex-end"
              marginRight={2}
              marginTop={{ base: 2, md: 0 }}
            >
              <HStack
                height={50}
                border={2}
                paddingLeft={2}
                paddingRight={2}
                borderStyle="solid"
                borderColor="grey"
                borderRadius="medium"
                justifyContent="center"
                backgroundColor="white"
                _hover={{
                  borderColor: 'brand',
                  boxShadow: '0 0 0 2px #5bce4c',
                }}
              >
                <IconButton
                  label="sort"
                  size="sm"
                  icon={<CustomSortIcon color="green.500" height="16px" />}
                  onClick={toggleSortOrder}
                />
              </HStack>
              <Dropdown
                options={sortOptions}
                value={sortBy}
                onChange={(value) => changeSortBy(value as SortOptionCondensed)}
                excludeSelected={true}
              />
            </HStack>
          </Flex>
        )}
        <PhoneListCondensed phones={sortPhones(phones)} onClick={onClick} />
      </>
    )
  },
)
